/* global googletag */
import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useMediaQuery } from '@/composables/useMediaQuery'
import useAdConfigurationGlobals from '@/composables/useAdConfigurationGlobals';

const { $GPT_PRIMARY_ACTIVITY, $AD_SOURCE, $AD_SLOT_INFO } = useAdConfigurationGlobals();

export let useAdStore = defineStore('adStore', () => {
  const isDesktop = useMediaQuery('(min-width: 1440px)');
  const isMedium = useMediaQuery('(min-width: 840px)');

  const adSlots = new Map();

  const refreshAllowed = ref(true);

  let gptInitialized = false;
  function initGpt() {
    if (gptInitialized) return;
    gptInitialized = true;

    window.googletag = window.googletag || { cmd: [] };
    googletag.cmd.push(() => {
      googletag.setConfig({ centering: false, disableInitialLoad: true })
      googletag.enableServices();
    });
  }

  const getAdSlot = computed(() => adId => adSlots.value[adId]);
  
  const primaryActivity = computed(() => $GPT_PRIMARY_ACTIVITY)
  const isAdsense = computed(() => $AD_SOURCE === 'adsense')
  const deviceSize = computed(() => {
    return isDesktop.value && isMedium.value
      ? 'desktop'
      : !isDesktop.value && isMedium.value
      ? 'medium'
      : 'mobile'
  });

  const slotNamePrefix = computed(() => position => $AD_SLOT_INFO[$AD_SOURCE][position][deviceSize.value].namePrefix);
  const adDimensions = computed(() => position => $AD_SLOT_INFO[$AD_SOURCE][position][deviceSize.value].dimensions);
  const adWidth = computed(() => position => adDimensions.value(position)[0]);
  const adHeight = computed(() => position => adDimensions.value(position)[1]);
  const slotName = computed(() => {
    return (position) => {
      const prefix = slotNamePrefix.value(position);

      return isAdsense.value ? `${prefix}${position}` : `${prefix}${primaryActivity.value}`;
    }
  });


  // Monotonic counter so ids stay unique even after slots are unregistered
  // (deriving ids from adSlots.size would reuse ids once entries are deleted).
  let adSlotCounter = 0;

  function registerAdSlot() {
    const adId = `banner-ad-${++adSlotCounter}`;

    adSlots.set(adId, null);

    return adId;
  }

  function defineAdSlot(settings) {
    const accountId = $AD_SLOT_INFO[$AD_SOURCE].accountId
    const slot = slotName.value(settings.position);
    const dimensions = adDimensions.value(settings.position);

    initGpt();

    googletag.cmd.push(() => {
      const adSlot = googletag
        .defineSlot(`/${accountId}/${slot}`, dimensions, settings.id)
        .addService(googletag.pubads());

      adSlots.set(settings.id, adSlot)
    });
  }

  // Tear down a slot when its BannerAd unmounts. Without this, remounts
  // (container list rebuilds, navigation on long-lived pages) accumulate dead
  // slots in GPT's registry and the adSlots map for the life of the page.
  // Runs through googletag.cmd so it's ordered after the slot's own
  // defineAdSlot callback.
  function unregisterAdSlot(id) {
    if (!adSlots.has(id)) return;

    googletag.cmd.push(() => {
      const adSlot = adSlots.get(id);

      if (adSlot) googletag.destroySlots([adSlot]);

      adSlots.delete(id);
    });
  }

  function loadAd(id) {
    googletag.cmd.push(() => {
      googletag.display(id)
      googletag.pubads().refresh([adSlots.get(id)])
    })    
  }

  function refreshAd(id) {
    if (!refreshAllowed.value) return;
    
    const adSlot = adSlots.get(id);
    
    if (!adSlot) return

    googletag.cmd.push(() => {
      googletag.pubads().refresh([adSlot])
    })

    blockRefresh();
  }  

  function blockRefresh(delay = 10000) {
    refreshAllowed.value = false

    setTimeout(() => {
      refreshAllowed.value = true
    }, delay)
  }
  
  return {
    adSlots,
    getAdSlot,
    deviceSize,
    adDimensions,
    slotName,
    adWidth,
    adHeight,
    registerAdSlot,
    unregisterAdSlot,
    defineAdSlot,
    loadAd,
    refreshAd
  }
})
